import getSymbolFromCurrency from 'currency-map-symbol';
import {mapGetters, mapState} from 'vuex';

export const formatMixin = {
  computed: {
    ...mapState('user', ['defaultCurrency']),
    ...mapGetters('user', ['getLocaleIso']),
    stringToHslColor() {
      return (str, s, l) => {
        let hash = 0;
        /* eslint-disable */
        const string = str ? str : '';
        for (let i = 0; i < string.length; i += 1) {
          hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        /* eslint-enable */
        const h = hash % 360;
        return `hsl(${h}, ${s}%, ${l}%)`;
      };
    },
    nameToSlug() {
      return (name, count = 3) => {
        const wordArray = name.split(' ');
        let slug = '';
        if (wordArray.length === 1) {
          slug = wordArray[0].slice(0, 2);
        } else if (wordArray.length === 2) {
          slug = wordArray[0].slice(0, 1);
          slug += wordArray[1][0];
        } else {
          wordArray.forEach((word) => {
            if (slug.length < count) {
              slug += word[0];
            }
          });
        }
        return slug;
      };
    },
    displayedCompanies() {
      return (obj) =>
        Object.entries(obj).sort((a, b) => {
          if (
            (a[1][0] ? a[1][0] : '').toLowerCase() >
            (b[1][0] ? b[1][0] : '').toLowerCase()
          ) {
            return 1;
          }
          if (
            (a[1][0] ? a[1][0] : '').toLowerCase() <
            (b[1][0] ? b[1][0] : '').toLowerCase()
          ) {
            return -1;
          }
          return 0;
        });
    },
    formatCurrencyInteger() {
      return (val, round = true) => {
        if (val === null || val === undefined) return val;

        let num = Number(val);
        if (isNaN(num)) return val;

        num = num < 1_000_000 ? num : Math.round(num);
        num = round ? Number(num.toFixed(0)) : Number(num.toFixed(2));

        return new Intl.NumberFormat(this.getLocaleIso).format(num);
      };
    },
    numberValueDecimal() {
      return (val) => (isNaN(Number(val)) || !val ? null : Number(val));
    },
    formatCurrencyFlexible() {
      return (val, maxAmount) =>
        Math.abs(+val) > maxAmount
          ? this.getValueWithCurrency(val).replace(/[^\d.,\s-]+/g, '')
          : this.getValueWithCurrency(val);
    },
    formatCurrencyIntegerWithDefaultCurrency() {
      // eslint-disable-next-line no-unused-vars
      return (val, currency = this.defaultCurrency) =>
        this.formatCurrency(val, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          currency,
        });
    },
    formatCurrencyWithDecimal() {
      return (val, cur) =>
        this.formatCurrency(val, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          currency: cur || this.defaultCurrency,
        });
    },
    formatCurrencyCompact() {
      return (val, currency = this.defaultCurrency) =>
        this.formatCurrency(val, {
          notation: 'compact',
          compactDisplay: 'short',
          currency,
        });
    },
    formatCurrency() {
      return (
        value,
        {
          currency = this.defaultCurrency,
          minimumFractionDigits = 0,
          maximumFractionDigits = 0,
          notation = 'standard',
          compactDisplay = 'long',
        } = {
          currency: this.defaultCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        },
      ) =>
        new Intl.NumberFormat(this.getLocaleIso, {
          minimumFractionDigits,
          maximumFractionDigits,
          style: 'currency',
          currency,
          notation,
          compactDisplay,
        }).format(value);
    },
    getSymbol() {
      return (cur) => getSymbolFromCurrency(cur);
    },
    normalize() {
      return (txt) =>
        txt
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
    },
    stringInclude() {
      return (a, b) => this.normalize(a).includes(this.normalize(b));
    },
    solde() {
      return (banks) => {
        if (banks && banks.length) {
          const total = banks.reduce((acc, el) => {
            const val = el.accounts.reduce(
              (accum, a) =>
                (a.balance ? a.balance : 0) / a.exchange_rate + accum,
              0,
            );
            return val + acc;
          }, 0);
          return total;
        }
        return 0;
      };
    },
  },
  methods: {
    getNumberSymbols() {
      const locale = this.getLocaleIso || 'en-GB';
      const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);

      const decimal = parts.find((p) => p.type === 'decimal')?.value || '.';
      const group = parts.find((p) => p.type === 'group')?.value || '';

      return {decimal, group};
    },
    parseLocalizedNumber(raw) {
      if (raw == null) return null;

      const {decimal: localeDec} = this.getNumberSymbols();

      let text = String(raw);
      if (!text) return null;

      const trimmed = text.trim();

      // --- sign ---
      let sign = '';
      if (trimmed.startsWith('-')) {
        sign = '-';
      }

      // only "-" / "+" → nothing to parse yet
      if (trimmed === '-' || trimmed === '+') {
        return {isEmpty: true, sign, trailingDecimal: false};
      }

      // trailing decimal char? (100, / 100.)
      const trailingDecimal = /[.,]$/.test(trimmed);

      // remove sign and all whitespace-like stuff
      let s = text.replace(/^[+-]/, '').replace(/[\s\u00A0\u202F]/g, '');

      // keep only digits + "." + ","
      s = s.replace(/[^0-9.,]/g, '');
      if (!s) {
        return {isEmpty: true, sign, trailingDecimal};
      }

      const hasDot = s.includes('.');
      const hasComma = s.includes(',');

      // --- decide which char is decimal in THIS input ---
      let decimalChar = null;

      // 1) if locale decimal appears → it wins
      if (s.includes(localeDec)) {
        decimalChar = localeDec;
      } else {
        // 2) single "." with 1–2 digits after → treat as decimal
        if (hasDot && (s.match(/\./g) || []).length === 1) {
          const idx = s.indexOf('.');
          const digitsAfter = (s.slice(idx + 1).match(/\d/g) || []).length;
          if (digitsAfter > 0 && digitsAfter <= 2) {
            decimalChar = '.';
          }
        }
        // 3) single "," with 1–2 digits after → treat as decimal
        if (!decimalChar && hasComma && (s.match(/,/g) || []).length === 1) {
          const idx = s.indexOf(',');
          const digitsAfter = (s.slice(idx + 1).match(/\d/g) || []).length;
          if (digitsAfter > 0 && digitsAfter <= 2) {
            decimalChar = ',';
          }
        }
        // 4) if both present and nothing above сработало → rightmost is decimal
        if (!decimalChar && hasDot && hasComma) {
          decimalChar = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
        }
      }

      if (localeDec === '.' && decimalChar === ',') {
        decimalChar = null;
      }

      let intRaw = '';
      let fracRaw = '';

      if (decimalChar) {
        const idx = s.lastIndexOf(decimalChar);
        intRaw = s.slice(0, idx);
        fracRaw = s.slice(idx + 1);
      } else {
        intRaw = s;
        fracRaw = '';
      }

      // integer: remove ALL "." and ","
      intRaw = intRaw.replace(/[.,]/g, '');
      // fraction: digits only, max 2 digits
      fracRaw = fracRaw.replace(/[.,]/g, '').replace(/\D/g, '').slice(0, 2);

      if (!intRaw && !fracRaw) {
        return {isEmpty: true, sign, trailingDecimal};
      }

      const intDigits = intRaw || '0';
      const fracDigits = fracRaw;

      let numStr = sign + intDigits;
      if (fracDigits) {
        numStr += '.' + fracDigits;
      }

      const num = Number(numStr);
      if (Number.isNaN(num)) {
        return {isEmpty: true, sign, trailingDecimal};
      }

      return {
        num,
        sign,
        intDigits,
        fracDigits,
        trailingDecimal,
        isEmpty: false,
      };
    },
    evalLocalizedExpression(raw) {
      if (raw == null) return 0;

      const str = String(raw);
      if (!str.trim()) return 0;

      const parts = str.split(/([+-])/);

      let total = 0;
      let sign = '+';

      for (const partRaw of parts) {
        const part = partRaw.trim();
        if (!part) continue;

        if (part === '+' || part === '-') {
          sign = part;
          continue;
        }

        const parsed = this.parseLocalizedNumber(part);
        if (!parsed || parsed.isEmpty) continue;

        let value = parsed.num;

        if (sign === '-') {
          value = -value;
        }

        total += value;
        sign = '+';
      }

      return total;
    },
    getSeparators() {
      // e.g. for fr-FR → decimal ','; for en-GB → decimal '.'
      const parts = new Intl.NumberFormat(this.getLocaleIso).formatToParts(1.2);
      return parts.find((p) => p.type === 'decimal')?.value || '.';
    },
    onBackspace(e) {
      const el = e.target;
      const {selectionStart, selectionEnd, value} = el;

      if (selectionStart !== selectionEnd) return;

      const pos = selectionStart;
      if (pos <= 0) return;

      const {group} = this.getNumberSymbols();
      const separators = [group, ' ', '\u00A0', '\u202F'];

      const charBefore = value[pos - 1];

      if (separators.includes(charBefore)) {
        e.preventDefault();

        let i = pos - 2;
        while (i >= 0 && !/\d/.test(value[i])) {
          i--;
        }
        if (i < 0) return;

        const newRaw = value.slice(0, i) + value.slice(i + 1);

        const formatted = this.replaceSeparators(newRaw);

        this.$nextTick(() => {
          this.value = formatted;
          el.value = formatted;

          const delta = value.length - formatted.length;
          const newPos = Math.max(0, pos - 1 - delta);
          el.setSelectionRange(newPos, newPos);
        });
      }
    },
    replaceSeparators(raw) {
      if (raw == null) return '';
      const str = String(raw);

      if (str === '' || str === '-') return str;

      const parsed = this.parseLocalizedNumber(str);
      if (!parsed || parsed.isEmpty) {
        return str;
      }

      const {num, fracDigits, trailingDecimal} = parsed;
      const locale = this.getLocaleIso || 'en-GB';
      const {decimal: localeDec} = this.getNumberSymbols();

      const fracLen = fracDigits ? fracDigits.length : 0;

      const formattedCore = new Intl.NumberFormat(locale, {
        minimumFractionDigits: fracLen,
        maximumFractionDigits: 2,
      }).format(num);

      if (trailingDecimal && !fracLen) {
        return formattedCore + localeDec;
      }

      return formattedCore;
    },
    replaceSeparatorsSign(raw) {
      if (raw == null) return '';
      const str = String(raw);

      if (str === '' || str === '-') return str;

      const locale = this.getLocaleIso || 'en-GB';
      const {decimal: localeDec} = this.getNumberSymbols();

      const tokens = [];
      let current = '';

      for (let i = 0; i < str.length; i++) {
        const ch = str[i];

        if (
          (ch === '+' || ch === '-') &&
          i !== 0 && // allow leading minus as sign
          !(str[i - 1] === '+' || str[i - 1] === '-')
        ) {
          // avoid splitting "- -5"

          if (current !== '') {
            tokens.push(current);
            current = '';
          }

          tokens.push(ch);
        } else {
          current += ch;
        }
      }
      if (current) tokens.push(current);

      const formattedTokens = tokens.map((token) => {
        // Operator stays as-is
        if (token === '+' || token === '-') return token;

        const trimmed = token.trim();
        if (!trimmed) return token;

        const parsed = this.parseLocalizedNumber(trimmed);
        if (!parsed || parsed.isEmpty) return token;

        const {num, fracDigits, trailingDecimal} = parsed;
        const fracLen = fracDigits ? fracDigits.length : 0;

        const formattedCore = new Intl.NumberFormat(locale, {
          minimumFractionDigits: fracLen,
          maximumFractionDigits: 2,
        }).format(num);

        const beforeSpaces = token.match(/^\s*/)?.[0] || '';
        const afterSpaces = token.match(/\s*$/)?.[0] || '';

        let final = formattedCore;
        if (trailingDecimal && !fracLen) final += localeDec;

        return beforeSpaces + final + afterSpaces;
      });

      return formattedTokens.join('');
    },
  },
};

export default {formatMixin};
